// Contact Us — the public marketing contact page (/contact). Uses the canonical marketing top bar
// and footer around its soft dual-glow contact surface, so navigation cannot drift from the rest of
// the marketing site. The form files a PLATFORM sales lead (POST /public/platform-enquiry,
// source="contact_page" — same pipeline the PlatformAdmin → Sales leads console works).
import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { m, useReducedMotion } from "framer-motion";
import axios from "axios";
import { installIdempotentRetry } from "../lib/httpRetry";
import { init as initAnalytics, track, trackPhoneCallClick, trackWhatsAppClick, utmLeadFields, whatsappUrlWithContext } from "../lib/analytics";
import { ALL_PHONES, SALES_EMAIL, SUPPORT_EMAIL } from "../lib/contactInfo";
import {
  CTA_GRADIENT,
  CTA_GRADIENT_HOVER,
  LEAD_ROLE_OPTIONS,
  LEAD_STUDENT_BANDS,
  MarketingTopBar,
  SiteFooter,
  withLeadRole,
} from "../components/marketing/shared";
import { MarketingMotion } from "../components/marketing/MarketingMotion";
import MarketingSeo, { seoOrigin } from "../components/marketing/MarketingSeo";
import { useMarketingContent } from "../lib/marketingContent";
import Captcha, { resetCaptcha } from "../components/Captcha";
import { CTA_PRIMARY_LABEL } from "../components/marketing/navigation";
import { ORGANIZATION_ID, organizationSchemaFor } from "../components/marketing/organizationSchema";
import {
  Phone, Mail, LifeBuoy, Sparkles, Send, CheckCircle2, ArrowRight, MessageCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
// INFRA-8: `/help` and `/contact` are PRERENDERED marketing routes, so scripts/prerender.mjs's
// proxy retry protects the SNAPSHOT — nothing protected the reader's browser, and one ambient
// ALB 502 on these public GETs renders the empty rail / "Couldn't open this article" toast.
// Same shared rule as every other transport (src/lib/httpRetry.js); the POST below is never
// replayed because the rule is idempotent-methods-only.
const pub = installIdempotentRetry(axios.create({ baseURL: API }));

const PRIMARY_CTA =
  `inline-flex items-center justify-center gap-2 whitespace-nowrap h-[44px] px-[24px] rounded-full text-white text-[14px] font-semibold ${CTA_GRADIENT} shadow-[0_10px_24px_rgba(29,78,216,0.22)] ${CTA_GRADIENT_HOVER} hover:-translate-y-px hover:shadow-[0_12px_28px_rgba(29,78,216,0.28)] active:scale-[0.98] transition-[transform,background-image,box-shadow] duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2`;
const SECONDARY_PILL =
  "inline-flex items-center justify-center gap-2 whitespace-nowrap h-[42px] px-[18px] rounded-full border border-[rgba(15,23,42,0.08)] bg-white/75 text-[#374151] text-[14px] font-medium shadow-[0_1px_2px_rgba(15,23,42,0.06)] hover:bg-white hover:-translate-y-px hover:border-orange-border/70 hover:shadow-[0_8px_20px_-6px_rgba(249,115,22,0.30)] active:scale-[0.98] transition-[transform,background-color,border-color,box-shadow] duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2";

const CONTACT_DESCRIPTION = "Book a free demo of the workflows your school runs, ask about pricing and rollout, or reach Vidyapeeth360 support — we reply on working days.";
// PGS-14: the page publishes the Organization node WITH its verified, purpose-labelled
// contactPoints (the same three lines + address rendered on this page) so the rich result
// carries real phones — never invented ratings/reviews (organizationSchema.js rules apply).
// A builder, not a frozen literal: the Organization node gains sameAs only when the CMS
// carries published social profiles (organizationSchemaFor, TRUST-15).
const contactSchemaFor = (socialLinks) => ({
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "ContactPage",
      name: "Contact Vidyapeeth360",
      description: CONTACT_DESCRIPTION,
      url: `${seoOrigin}/contact`,
      about: { "@id": ORGANIZATION_ID },
    },
    {
      ...organizationSchemaFor(socialLinks),
      contactPoint: ALL_PHONES.map((phone, index) => ({
        "@type": "ContactPoint",
        telephone: phone.tel.replace("+91", "+91-"),
        contactType: index === 0 ? "sales" : index === 1 ? "customer onboarding" : "customer support",
        areaServed: "IN",
        availableLanguage: ["en", "hi"],
      })),
    },
  ],
});

const TOUR_REQUEST_MESSAGE = "I'd like to book a live demo and request an approved product walkthrough for our school.";

// WP-19: the Contact page publishes the same three verified lines as the footer, labelled by
// purpose. WhatsApp stays on the primary sales number from lib/contactInfo.
const CHANNELS = [
  {
    icon: Phone, accent: "text-primary bg-primary/10", title: "Call us",
    desc: "Sales first, then direct lines for onboarding and support follow-ups.",
    lines: ALL_PHONES.map((phone, index) => ({
      label: `${phone.display}${index === 0 ? " · sales" : index === 1 ? " · onboarding" : " · support"}`,
      href: `tel:${phone.tel}`,
      // CRO-10: the labelled purpose rides the event, never the number.
      line: index === 0 ? "sales" : index === 1 ? "onboarding" : "support",
    })),
  },
  {
    icon: MessageCircle, accent: "text-positive bg-positive-soft", title: "WhatsApp us",
    desc: "Message the sales line on WhatsApp — the same team, on the channel most Indian school offices already use.",
    // CRO-11: same number, with the evaluation context prefilled (visible + editable in WhatsApp).
    lines: [{ label: "Chat on WhatsApp", href: whatsappUrlWithContext("the Contact page"), external: true }],
  },
  {
    icon: Mail, accent: "text-sky-dark bg-sky/10", title: "Email us",
    desc: "Sales questions and product support — pick the inbox that fits.",
    lines: [
      { label: `${SALES_EMAIL} · sales & pricing`, href: `mailto:${SALES_EMAIL}` },
      { label: `${SUPPORT_EMAIL} · product support`, href: `mailto:${SUPPORT_EMAIL}` },
    ],
  },
  {
    icon: LifeBuoy, accent: "text-orange-dark bg-orange-soft", title: "Help Center",
    desc: "Guides and answers for setup, migration, fees, compliance and more.",
    lines: [{ label: "Browse the Help Center", href: "/help", internal: true }],
  },
  {
    icon: Sparkles, accent: "text-ai-ink bg-ai/10", title: "Ask Aira",
    desc: "Open the on-page assistant for supported product and school-workflow questions.",
    lines: [{ label: "Chat with Aira now", action: "aira" }],
  },
];

export default function Contact() {
  const reduce = useReducedMotion();
  const navigate = useNavigate();
  const location = useLocation();
  // Hero copy is CMS-owned (cms.contact_page); literals below byte-mirror the backend DEFAULTS.
  const cms = useMarketingContent();
  const contactCms = cms?.contact_page || {};
  const heroHeading = contactCms.heading || "Talk to a real person about your school";
  const heroHighlight = contactCms.highlight || "real person";
  const heroParts = heroHeading.includes(heroHighlight)
    ? [heroHeading.slice(0, heroHeading.indexOf(heroHighlight)), heroHighlight,
       heroHeading.slice(heroHeading.indexOf(heroHighlight) + heroHighlight.length)]
    : [heroHeading, "", ""];
  const tourRequest = new URLSearchParams(location.search).get("intent") === "product-tour";
  React.useEffect(() => {
    initAnalytics(); // marketing analytics — idempotent, env-gated, captures UTM attribution
  }, []);
  const [f, setF] = useState({
    contact_name: "",
    school_name: "",
    email: "",
    phone: "",
    message: tourRequest ? TOUR_REQUEST_MESSAGE : "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [emailErr, setEmailErr] = useState("");
  // CRO-8: optional size qualification — one select row, never gates submit. The value is the
  // band ceiling (an approximate size signal the CRM displays), not a head count.
  const [studentBand, setStudentBand] = useState("");
  const [buyerRole, setBuyerRole] = useState("");
  const successRef = React.useRef(null);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  React.useEffect(() => {
    // React Router keeps this page mounted when only the query string changes. Reset the
    // submission state and update only our untouched intent seed; never erase authored copy.
    setDone(false);
    setError("");
    setF((current) => {
      if (tourRequest && !current.message.trim()) {
        return { ...current, message: TOUR_REQUEST_MESSAGE };
      }
      if (!tourRequest && current.message === TOUR_REQUEST_MESSAGE) {
        return { ...current, message: "" };
      }
      return current;
    });
  }, [tourRequest]);
  const emailOk = !f.email.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim());
  // Captcha is enforced SERVER-side when configured (fails closed there); the client never hard-
  // blocks on the widget so a slow/blocked challenges.cloudflare.com can't dead-end the form.
  const valid = f.school_name.trim() && (f.email.trim() || f.phone.trim()) && emailOk;

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!valid) {
      setError(emailOk
        ? "Add your school's name and either an email address or a phone number so we can reply."
        : "That email address doesn't look right — check it or use a phone number instead.");
      // FORM-009: name the rule AND take the visitor to the field that breaks it — a chatty line
      // at the foot of a long mobile form gave them nothing to act on.
      document.getElementById(f.school_name.trim() ? "ct-email" : "ct-school")?.focus();
      return;
    }
    setBusy(true); setError("");
    try {
      // utmLeadFields → the backend's whitelisted `campaign` field; spread FIRST so form/source keys always win
      await pub.post("/public/platform-enquiry", {
        ...utmLeadFields(),
        ...f,
        // CRO-8: the optional buyer role folds into `message` (the enquiry model forbids extra
        // keys); one fold shape for all three lead forms (components/marketing/shared.js).
        message: withLeadRole(f.message, buyerRole),
        ...(studentBand ? { student_count: Number(studentBand) } : {}),
        source: tourRequest ? "product_tour_request" : "contact_page",
        captcha_token: captchaToken || undefined,
      });
      // WP-06 canonical conversion — the contact form previously fired no event at all.
      track("demo_form_submitted", { placement: tourRequest ? "product_tour_request" : "contact_page" });
      setDone(true);
      setTimeout(() => successRef.current?.focus(), 60); // move focus to the confirmation (a11y)
    } catch (err) {
      // CRO-5: a prod form outage must be analytically visible, not silent.
      track("lead_submit_failed", {
        placement: tourRequest ? "product_tour_request" : "contact_page",
        http_status: err?.response?.status ?? 0,
      });
      setError(`Couldn't send your message right now. Please try again, or email ${SUPPORT_EMAIL}.`);
    } finally { resetCaptcha(); setBusy(false); }
  };

  const field = "min-h-11 w-full rounded-xl border border-line bg-surface px-3.5 py-2.5 text-sm text-ink placeholder:text-ink-subtle focus:outline-none focus:border-accent focus:ring-2 focus:ring-primary/25 transition";

  return (
    <MarketingMotion>
    <div className="relative min-h-screen bg-surface landing-page">
      <MarketingSeo
        title="Contact Us — Book a Free Demo | Vidyapeeth360"
        description={CONTACT_DESCRIPTION}
        path="/contact"
        schema={contactSchemaFor(cms?.social_links?.links)}
      />
      {/* Soft dual-glow background — static depth that matches the Help Center. */}
      <div className="pointer-events-none absolute top-0 left-0 right-0 h-[640px] overflow-hidden" aria-hidden="true">
        <div className="absolute -top-28 -left-24 w-[540px] h-[540px] rounded-full bg-primary/10 blur-[130px]" />
        <div className="absolute top-24 -right-24 w-[480px] h-[480px] rounded-full bg-orange/10 blur-[130px]" />
      </div>

      {/* The general contact form remains available below; the sitewide demo action uses the same
          focused dialog as every other marketing page so its fields and follow-up promise do not
          change just because the visitor happens to be on /contact. */}
      <MarketingTopBar />

      {/* Hero */}
      <section className="relative">
        <div className="max-w-3xl mx-auto px-5 pt-12 pb-8 sm:pt-16 sm:pb-10 text-center">
          <m.span className="inline-flex items-center gap-2 rounded-full border border-orange-border/60 bg-orange-soft px-3.5 py-1.5 text-[13px] font-semibold text-orange-text"
            initial={reduce ? false : { opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, ease: "easeOut" }}>
            <MessageCircle className="w-4 h-4" aria-hidden="true" /> {tourRequest ? "Reviewed product tour" : "Contact Us"}
          </m.span>
          <m.h1 className="mt-4 font-heading font-extrabold text-[32px] sm:text-[46px] text-ink leading-[1.08] tracking-tight"
            initial={reduce ? false : { opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: "easeOut", delay: 0.06 }}>
            {tourRequest ? <>Request a <span className="text-primary">guided product tour</span></> : <>{heroParts[0]}{heroParts[1] ? <span className="text-primary">{heroParts[1]}</span> : null}{heroParts[2]}</>}
          </m.h1>
          <m.p className="mt-3.5 text-ink-2 text-[16px] sm:text-[17px] leading-relaxed max-w-xl mx-auto"
            initial={reduce ? false : { opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: "easeOut", delay: 0.12 }}>
            {tourRequest
              ? "Public product recordings are not exposed before review. Book a demo below; after our team reviews the request, we will confirm the session and approved walkthrough access."
              : (contactCms.subhead || "Questions about plans, a guided demo, onboarding help — our sales and support team is a call, an email or one short form away.")}
          </m.p>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-5 pb-24 lg:pb-16 grid lg:grid-cols-2 gap-8 items-start">
        {/* Channels */}
        <div className="order-last lg:order-none grid sm:grid-cols-2 gap-4">
          {CHANNELS.map((c, i) => (
            <m.div key={c.title} initial={reduce ? false : { opacity: 0, y: 16 }} whileInView={reduce ? undefined : { opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.45, ease: "easeOut", delay: Math.min(i * 0.06, 0.25) }}
              className="rounded-2xl border border-line bg-surface p-5 hover:shadow-elevated hover:-translate-y-0.5 transition-[transform,box-shadow] duration-200">
              <div className={`w-11 h-11 rounded-xl grid place-items-center mb-3 ${c.accent}`}><c.icon className="w-5 h-5" aria-hidden="true" /></div>
              <h2 className="font-heading font-semibold text-ink">{c.title}</h2>
              <p className="text-[13.5px] text-ink-subtle mt-1 leading-relaxed">{c.desc}</p>
              <ul className="mt-3 space-y-1.5">
                {c.lines.map((l) => (
                  <li key={l.label}>
                    {l.action === "aira" ? (
                      <button type="button" onClick={() => window.dispatchEvent(new CustomEvent("vp-aira-open"))} data-testid="contact-open-aira"
                        className="inline-flex min-h-11 items-center gap-1.5 rounded text-sm font-semibold text-ai-ink hover:underline focus:outline-none focus-visible:ring-2 ring-ai/50">
                        {l.label} <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                      </button>
                    ) : l.internal ? (
                      <Link to={l.href} className="inline-flex min-h-11 items-center gap-1.5 rounded text-sm font-semibold text-accent-active hover:underline focus:outline-none focus-visible:ring-2 ring-primary">
                        {l.label} <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                      </Link>
                    ) : (
                      <a href={l.href} target={l.external ? "_blank" : undefined} rel={l.external ? "noopener noreferrer" : undefined}
                        onClick={l.href?.startsWith("https://wa.me/") ? () => trackWhatsAppClick("contact_page")
                          : l.line ? () => trackPhoneCallClick("contact_page", l.line) : undefined}
                        className="inline-flex min-h-11 items-center rounded text-sm font-semibold text-accent-active hover:underline focus:outline-none focus-visible:ring-2 ring-primary">{l.label}</a>
                    )}
                  </li>
                ))}
              </ul>
            </m.div>
          ))}
        </div>

        {/* Form */}
        <m.div initial={reduce ? false : { opacity: 0, y: 16 }} whileInView={reduce ? undefined : { opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="rounded-3xl border border-line bg-surface p-6 sm:p-7 shadow-[0_18px_50px_-16px_rgba(15,23,42,0.18)]" data-testid="contact-form-card">
          {done ? (
            <div className="text-center py-10" role="status" data-testid="contact-success" ref={successRef} tabIndex={-1}>
              <div className="w-14 h-14 rounded-2xl bg-positive-soft text-positive grid place-items-center mx-auto mb-4"><CheckCircle2 className="w-7 h-7" aria-hidden="true" /></div>
              <h2 className="font-heading font-bold text-ink text-xl">Thank you!</h2>
              <p className="text-ink-2 mt-2 max-w-sm mx-auto leading-relaxed">
                {tourRequest
                  ? "Your demo and tour request is with our team. We will review it before confirming the session or sharing walkthrough access."
                  : "Your message is with our team — a sales and support specialist will reply on a working day using the contact method you provided."}
              </p>
              <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
                <Link to="/help" className={`${SECONDARY_PILL} w-full sm:w-auto`}>Browse the Help Center</Link>
                <button type="button" onClick={() => navigate("/register")} className={`${PRIMARY_CTA} w-full sm:w-auto`}>{CTA_PRIMARY_LABEL}</button>
              </div>
            </div>
          ) : (
            <>
              <h2 className="font-heading font-bold text-ink text-xl">{tourRequest ? "Book the demo first" : "Send us a message"}</h2>
              <p className="text-sm text-ink-subtle mt-1">
                {tourRequest
                  ? "Tell us about your school and the workflows you want to review. Tour access is confirmed only after the request is reviewed."
                  : "Tell us a little about your school — we'll reply on working days using the contact method you provide."}
              </p>
              <form onSubmit={submit} noValidate className="mt-5 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="ct-name" className="block text-[13px] font-medium text-ink-2 mb-1.5">Your name <span className="text-ink-subtle font-normal">(optional)</span></label>
                    <input id="ct-name" autoComplete="name" value={f.contact_name} onChange={(e) => set("contact_name", e.target.value)} placeholder="Full name" className={field} data-testid="contact-name" />
                  </div>
                  <div>
                    <label htmlFor="ct-school" className="block text-[13px] font-medium text-ink-2 mb-1.5">School name <span className="text-danger" aria-hidden="true">*</span></label>
                    <input id="ct-school" autoComplete="organization" required value={f.school_name} onChange={(e) => set("school_name", e.target.value)} placeholder="Your school's name" className={field} data-testid="contact-school" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="ct-email" className="block text-[13px] font-medium text-ink-2 mb-1.5">Email</label>
                    <input id="ct-email" type="email" autoComplete="email" value={f.email}
                      onChange={(e) => { set("email", e.target.value); if (emailErr) setEmailErr(""); }}
                      onBlur={() => setEmailErr(emailOk ? "" : "Enter a valid email address.")}
                      aria-invalid={!!emailErr} aria-describedby={emailErr ? "ct-contact-hint ct-email-err" : "ct-contact-hint"}
                      placeholder="principal@yourschool.in" className={field} data-testid="contact-email" />
                    {emailErr && <p id="ct-email-err" role="alert" className="text-[12px] text-danger mt-1">{emailErr}</p>}
                  </div>
                  <div>
                    <label htmlFor="ct-phone" className="block text-[13px] font-medium text-ink-2 mb-1.5">Phone</label>
                    <input id="ct-phone" type="tel" autoComplete="tel" value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+91 98765 43210" className={field} aria-describedby="ct-contact-hint" data-testid="contact-phone" />
                  </div>
                  <p id="ct-contact-hint" className="sm:col-span-2 -mt-1 text-[12.5px] text-ink-subtle">Give us at least one of these — an email address or a phone number.</p>
                </div>
                {/* CRO-8: one optional row of light qualification so sales stops sizing every
                    lead by phone from zero. Neither select gates submit; the vocabulary is
                    shared with the landing demo form and the site-wide demo modal. */}
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="ct-students" className="block text-[13px] font-medium text-ink-2 mb-1.5">Number of students <span className="text-ink-subtle font-normal">(optional, approximate)</span></label>
                    <select id="ct-students" value={studentBand} onChange={(e) => setStudentBand(e.target.value)} className={field} data-testid="contact-students">
                      <option value="">Select a range</option>
                      {LEAD_STUDENT_BANDS.map((band) => <option key={band.value} value={band.value}>{band.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="ct-role" className="block text-[13px] font-medium text-ink-2 mb-1.5">Your role <span className="text-ink-subtle font-normal">(optional)</span></label>
                    <select id="ct-role" value={buyerRole} onChange={(e) => setBuyerRole(e.target.value)} className={field} data-testid="contact-role">
                      <option value="">Select your role</option>
                      {LEAD_ROLE_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label htmlFor="ct-msg" className="block text-[13px] font-medium text-ink-2 mb-1.5">How can we help? <span className="text-ink-subtle font-normal">(optional)</span></label>
                  <div role="group" className="flex flex-wrap gap-1.5 mb-2" aria-label="Common topics">
                    {[
                      ["Book a guided demo", "I'd like a guided demo for our school. "],
                      ["Pricing & plans", "Please share plan pricing for our school. "],
                      ["Switching from another ERP", "We currently use other software and want to understand migration. "],
                    ].map(([label, starter]) => (
                      <button key={label} type="button" data-testid={`contact-chip-${label.split(" ")[0].toLowerCase()}`}
                        onClick={() => { set("message", starter); document.getElementById("ct-msg")?.focus(); }}
                        className="inline-flex min-h-11 items-center rounded-full border border-line bg-muted/60 px-3 py-2 text-[12.5px] font-medium text-ink-2 transition-colors hover:border-primary/40 hover:text-accent-active focus:outline-none focus-visible:ring-2 ring-primary">
                        {label}
                      </button>
                    ))}
                  </div>
                  <textarea id="ct-msg" rows={4} value={f.message} onChange={(e) => set("message", e.target.value)} placeholder="We're evaluating a school ERP for the next academic year…" className={`${field} resize-none`} data-testid="contact-message" />
                </div>

                {error && <p role="alert" className="text-sm text-danger">{error}</p>}
                <Captcha onToken={setCaptchaToken} action="contact" className="flex justify-center" />
                <button type="submit" disabled={busy} data-testid="contact-submit"
                  className={`${PRIMARY_CTA} w-full disabled:opacity-60 disabled:pointer-events-none`}>
                  {busy ? "Sending…" : <>{tourRequest ? "Send demo request" : "Send message"} <Send className="w-4 h-4" aria-hidden="true" /></>}
                </button>
                {/* WP-11 — DPDP notice at the point of collection, inside the form container. */}
                <p className="mt-3 text-[12.5px] leading-relaxed text-ink-subtle" data-testid="contact-privacy-note">
                  We'll use these details, and how you reached this page, only to answer your enquiry. See our{" "}
                  <Link to="/privacy-policy" className="font-semibold text-accent hover:underline">Privacy Policy</Link>.
                  You can ask us to delete them at any time.
                </p>
              </form>
            </>
          )}
        </m.div>
      </main>

      <SiteFooter />
    </div>
    </MarketingMotion>
  );
}
